import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { KadmiLogo } from '../components/KadmiLogo';
import {
  Sparkles, ArrowRight, CheckCircle, Shield, Zap, Lock, Star, Globe,
  FileText, Building2, Landmark, Car, Receipt, Banknote, Briefcase, XCircle, HelpCircle,
  Loader2, AlertTriangle, Clock, Sun, Moon, Menu, X,
  Download, Mail, Share2, Phone, Copy, Pencil, ClipboardPaste, Camera,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import analytics from '../services/analytics';
import LanguageSelector from '../components/LanguageSelector';
import HeroAnimation from '../components/HeroAnimation';
import FeatureShowcase from '../components/FeatureShowcase';
import ChatbotDemo from '../components/ChatbotDemo';
import PwaInstallBanner from '../components/PwaInstallBanner';
import LaunchBanner from '../components/LaunchBanner';
import TrustBadges from '../components/TrustBadges';
import { stats } from '../content/stats';
import { temoignages } from '../content/temoignages';
import { DOMAINS } from '../data/domainsData';
import '../styles/landing-bg.css';

const V = '#7FB342';
const ICONS = { prefecture: Building2, caf: Landmark, amende: Car, impots: Receipt, banque: Banknote, travail: Briefcase, resiliation: XCircle, autre: HelpCircle };
const ACCENTS = { prefecture: '#7FB342', caf: '#2563EB', amende: '#7FB342', impots: '#059669', banque: '#DC2626', travail: '#0EA5E9', resiliation: '#475569', autre: '#9333EA' };
const CATS = ['prefecture', 'caf', 'amende', 'impots', 'banque', 'travail', 'resiliation', 'sav', 'transport', 'logement', 'facture', 'autre'];

const LandingPageV2 = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated } = useAuth();
  const { t, i18n } = useTranslation();
  const isDark = theme === 'dark';
  const isRTL = i18n.language === 'ar';
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [demoText, setDemoText] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  const [sampleLetterLoading, setSampleLetterLoading] = useState(false);
  const [sampleLetter, setSampleLetter] = useState(null);
  const [sampleLetterError, setSampleLetterError] = useState(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [exitPopup, setExitPopup] = useState(false);
  const exitShown = useRef(false);

  useEffect(() => { analytics.pageView('landing_v3'); }, []);

  // Exit intent (desktop only, non-authenticated)
  useEffect(() => {
    if (isAuthenticated) return;
    const handler = (e) => {
      if (e.clientY < 5 && !exitShown.current) {
        exitShown.current = true;
        setExitPopup(true);
      }
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [isAuthenticated]);

  // Demo placeholders rotation
  const placeholders = [
    "La CAF m'informe de la suspension de mes allocations...",
    "J'ai reçu une amende majorée de 375€...",
    "La préfecture a refusé mon renouvellement..."
  ];
  const [phIdx, setPhIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setPhIdx(i => (i + 1) % placeholders.length), 3000);
    return () => clearInterval(t);
  }, []);

  const handleDemo = async () => {
    if (!demoText.trim()) return;
    setDemoLoading(true);
    setDemoResult(null);
    setSampleLetter(null);
    setSampleLetterError(null);
    try {
      const res = await api.post('/letters/analyze', { text: demoText.trim() });
      setDemoResult(res.data);
    } catch (e) {
      setDemoResult({ resume: "Veuillez détailler davantage votre situation.", action: "Réessayez.", urgence: "moyenne" });
    }
    setDemoLoading(false);
  };

  const handleSampleLetter = async () => {
    if (!demoText.trim() || demoText.trim().length < 20) {
      setSampleLetterError("Décrivez votre situation en quelques phrases (20 caractères min).");
      return;
    }
    setSampleLetterLoading(true);
    setSampleLetter(null);
    setSampleLetterError(null);
    setShareCopied(false);
    try {
      const res = await api.post('/demo/sample-letter', { situation: demoText.trim(), language: i18n.language || 'fr' });
      setSampleLetter(res.data);
      analytics.track && analytics.track('sample_letter_generated', { categorie: res.data.categorie });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.error === 'sample_letter_daily_cap') {
        setSampleLetterError(detail.message);
      } else {
        setSampleLetterError("Génération indisponible. Réessayez dans quelques instants ou inscrivez-vous gratuitement.");
      }
    }
    setSampleLetterLoading(false);
  };

  const buildShareText = () => {
    if (!sampleLetter) return '';
    const arg = sampleLetter.argument_juridique_cle || '';
    const argTrimmed = arg.length > 100 ? arg.slice(0, 100).trimEnd() + '…' : arg;
    return `J'ai testé KadmiAI et c'est bluffant : 30s pour générer un courrier-exemple avec ${argTrimmed ? "l'argument juridique : " + argTrimmed : 'tous les bons articles de loi'}. Essayez gratuitement 👉`;
  };

  const handleShareCopy = async () => {
    const url = `https://kadmi-ai.com/?utm_source=share_letter&utm_medium=copy&utm_campaign=viral_${sampleLetter?.categorie || 'demo'}`;
    const text = `${buildShareText()} ${url}`;
    try {
      await navigator.clipboard.writeText(text);
      setShareCopied(true);
      analytics.track && analytics.track('sample_letter_shared', { channel: 'copy', categorie: sampleLetter?.categorie });
      setTimeout(() => setShareCopied(false), 2500);
    } catch {
      // iOS Safari / non-secure context fallback
      setSampleLetterError("Copie indisponible — sélectionnez le lien manuellement : " + url);
      setTimeout(() => setSampleLetterError(null), 5000);
    }
  };

  const shareUrl = (channel) => {
    const url = `https://kadmi-ai.com/?utm_source=share_letter&utm_medium=${channel}&utm_campaign=viral_${sampleLetter?.categorie || 'demo'}`;
    const text = buildShareText();
    analytics.track && analytics.track('sample_letter_shared', { channel, categorie: sampleLetter?.categorie });
    if (channel === 'whatsapp') return `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
    if (channel === 'twitter') return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    if (channel === 'linkedin') return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    return url;
  };

  const bg = isDark ? '#0F0F1A' : '#F7F6FF';
  const cardBg = isDark ? '#1A1A2E' : '#FFFFFF';
  const inputBg = isDark ? '#252538' : '#FFFFFF';
  const tp = isDark ? '#F0EFFF' : '#1A1A2E';
  const ts = isDark ? '#9998B8' : '#6B6B8A';
  const bd = isDark ? '#2E2E45' : '#E5E3F0';
  const visibleTemoignages = temoignages.filter(t => t.visible);

  return (
    <div className="min-h-screen transition-colors duration-300" style={{ background: bg, color: tp }}>
      <Helmet>
        <link rel="canonical" href="https://kadmi-ai.com/" />
      </Helmet>

      {/* ═══ LAUNCH BANNER (top, above navbar) ═══ */}
      <div className="fixed top-0 left-0 right-0 z-[60]">
        <LaunchBanner />
      </div>

      {/* ═══ NAVBAR ═══ */}
      <nav className="fixed top-9 w-full z-50 backdrop-blur-xl h-16" style={{ background: isDark ? 'rgba(15,15,26,0.9)' : 'rgba(247,246,255,0.92)', borderBottom: `1px solid ${bd}` }}>
        <div className="max-w-6xl mx-auto px-4 h-full flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <KadmiLogo size={32} />
            <span className="font-heading text-lg" style={{ color: tp }}>KadmiAI</span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium" style={{ color: ts }}>
            <a href="#comment" className="hover:text-[#7FB342] transition-colors">{t('landing_v3.nav_comment')}</a>
            <a href="#tarifs" className="hover:text-[#7FB342] transition-colors">{t('landing_v3.nav_tarifs')}</a>
            <a href="#faq" className="hover:text-[#7FB342] transition-colors">{t('landing_v3.nav_faq')}</a>
          </div>
          <div className="flex items-center gap-2">
            {/* Theme toggle (always visible - desktop AND mobile) */}
            <button onClick={toggleTheme} className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors hover:bg-white/5" style={{ color: ts }} data-testid="theme-toggle" aria-label="Toggle theme">
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            {/* Desktop only: language selector */}
            <div className="hidden md:flex items-center">
              <LanguageSelector compact />
            </div>
            {isAuthenticated ? (
              <Link to="/dashboard"><Button size="sm" style={{ background: V }} className="text-white font-semibold">{t('landing_v3.nav_dashboard')}</Button></Link>
            ) : (
              <>
                <Link to="/login" className="hidden sm:block"><Button variant="outline" size="sm" className="font-medium" style={{ borderColor: V, color: V }} data-testid="header-login-btn">{t('landing_v3.nav_login')}</Button></Link>
                <Link to="/register"><Button size="sm" className="text-white font-semibold" style={{ background: V, boxShadow: '0 2px 12px rgba(30,91,224,0.3)' }} data-testid="header-cta-btn">{t('landing_v3.nav_cta')}</Button></Link>
              </>
            )}
            <button onClick={() => setMobileMenu(!mobileMenu)} className="md:hidden p-2" style={{ color: ts }} aria-label="Open menu" data-testid="mobile-menu-toggle">{mobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}</button>
          </div>
        </div>
        {/* Mobile menu */}
        <AnimatePresence>{mobileMenu && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="md:hidden absolute top-16 left-0 right-0 p-4 space-y-1" style={{ background: cardBg, borderBottom: `1px solid ${bd}` }} data-testid="mobile-menu-panel">
            <a href="#comment" onClick={() => setMobileMenu(false)} className="block py-3 font-medium border-b" style={{ color: tp, borderColor: bd }}>{t('landing_v3.nav_comment')}</a>
            <a href="#tarifs" onClick={() => setMobileMenu(false)} className="block py-3 font-medium border-b" style={{ color: tp, borderColor: bd }}>{t('landing_v3.nav_tarifs')}</a>
            <a href="#faq" onClick={() => setMobileMenu(false)} className="block py-3 font-medium border-b" style={{ color: tp, borderColor: bd }}>{t('landing_v3.nav_faq')}</a>
            {!isAuthenticated && (
              <Link to="/login" onClick={() => setMobileMenu(false)} className="block py-3 font-semibold border-b" style={{ color: V, borderColor: bd }} data-testid="mobile-menu-login">{t('landing_v3.nav_login')}</Link>
            )}
            {/* Language + theme in mobile menu */}
            <div className="flex items-center justify-between pt-3">
              <div onClick={() => setTimeout(() => setMobileMenu(false), 50)}>
                <LanguageSelector />
              </div>
              <button onClick={toggleTheme} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium" style={{ color: ts, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)' }} data-testid="mobile-theme-toggle" aria-label="Toggle theme">
                {isDark ? <><Sun className="h-4 w-4" /> {t('common.light_mode', 'Clair')}</> : <><Moon className="h-4 w-4" /> {t('common.dark_mode', 'Sombre')}</>}
              </button>
            </div>
          </motion.div>
        )}</AnimatePresence>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="pt-[7.5rem] sm:pt-32 pb-12 px-4 lb-section">
        <div className={`lb-layer ${isDark ? 'lb-hero-dark' : 'lb-hero-light'}`}>
          {isDark ? <div className="lb-aurora" /> : <div className="lb-aurora-light" />}
          <div className="lb-bubble lb-b1" />
          <div className="lb-bubble lb-b2" />
          <div className="lb-bubble lb-b3" />
          <div className={isDark ? 'lb-grid-dark' : 'lb-grid-light'} />
          {isDark ? (
            <>
              <div className="lb-star" style={{ width: 3, height: 3, top: '8%', left: '12%', '--dur': '4s', '--delay': '0s' }} />
              <div className="lb-star" style={{ width: 2, height: 2, top: '15%', left: '78%', '--dur': '6s', '--delay': '1s' }} />
              <div className="lb-star" style={{ width: 4, height: 4, top: '35%', left: '5%', '--dur': '5s', '--delay': '0.5s' }} />
              <div className="lb-star" style={{ width: 2, height: 2, top: '50%', left: '90%', '--dur': '7s', '--delay': '2s' }} />
              <div className="lb-star" style={{ width: 3, height: 3, top: '65%', left: '22%', '--dur': '4.5s', '--delay': '1.5s' }} />
              <div className="lb-star" style={{ width: 2, height: 2, top: '20%', left: '55%', '--dur': '8s', '--delay': '0.3s' }} />
              <div className="lb-star" style={{ width: 3, height: 3, top: '72%', left: '68%', '--dur': '3.5s', '--delay': '2.5s' }} />
              <div className="lb-star" style={{ width: 2, height: 2, top: '45%', left: '42%', '--dur': '6.5s', '--delay': '1.2s' }} />
              <div className="lb-star" style={{ width: 4, height: 4, top: '80%', left: '85%', '--dur': '5.5s', '--delay': '0.8s' }} />
              <div className="lb-star" style={{ width: 2, height: 2, top: '10%', left: '35%', '--dur': '7.5s', '--delay': '3s' }} />
              <div className="lb-star" style={{ width: 3, height: 3, top: '55%', left: '15%', '--dur': '4s', '--delay': '1.8s' }} />
              <div className="lb-star" style={{ width: 2, height: 2, top: '88%', left: '50%', '--dur': '6s', '--delay': '0.6s' }} />
            </>
          ) : (
            <>
              <div className="lb-particle" style={{ width: 5, height: 5, top: '10%', left: '15%', '--dur': '5s', '--delay': '0s' }} />
              <div className="lb-particle" style={{ width: 4, height: 4, top: '25%', left: '75%', '--dur': '7s', '--delay': '1s' }} />
              <div className="lb-particle" style={{ width: 6, height: 6, top: '50%', left: '8%', '--dur': '6s', '--delay': '0.5s' }} />
              <div className="lb-particle" style={{ width: 3, height: 3, top: '60%', left: '88%', '--dur': '8s', '--delay': '2s' }} />
              <div className="lb-particle" style={{ width: 5, height: 5, top: '75%', left: '30%', '--dur': '5.5s', '--delay': '1.5s' }} />
              <div className="lb-particle" style={{ width: 4, height: 4, top: '15%', left: '55%', '--dur': '6.5s', '--delay': '0.3s' }} />
            </>
          )}
        </div>
        <div className="lb-content max-w-[680px] mx-auto text-center">
          <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} className="font-heading text-[2rem] sm:text-[2.6rem] md:text-[3.2rem] leading-[1.15] mb-5" style={{ color: tp }}>
            {t('landing_v3.hero_title').split('\n').map((line, i) => <span key={i}>{i > 0 && <br />}{line.includes('30 secondes') ? <>{line.replace('30 secondes', '')}<span style={{ color: V, textDecoration: 'underline', textDecorationColor: V, textUnderlineOffset: '6px' }}>30 secondes</span></> : line}</span>)}
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="text-base sm:text-lg leading-relaxed max-w-[520px] mx-auto mb-4" style={{ color: ts }}>
            {t('landing_v3.hero_subtitle')}
          </motion.p>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.25 }}>
            <HeroAnimation />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <Link to={isAuthenticated ? "/dashboard" : "/register"} onClick={() => analytics.ctaEssayerGratuitement('hero')}>
              <button className="w-full sm:w-auto px-10 py-4 rounded-[14px] text-white font-semibold text-base transition-all hover:-translate-y-0.5" style={{ background: V, boxShadow: `0 4px 20px rgba(30,91,224,0.35)` }} data-testid="hero-cta">
                <Sparkles className="h-4 w-4 inline mr-2" />{isAuthenticated ? t('landing_v3.hero_cta_connected') : t('landing_v3.hero_cta')}
              </button>
            </Link>
            {!isAuthenticated && <p className="text-xs mt-3" style={{ color: ts }}>{t('landing_v3.hero_micro')}</p>}
          </motion.div>
          {/* Trust badges */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium" style={{ background: isDark ? 'rgba(30,91,224,0.1)' : 'rgba(30,91,224,0.06)', color: isDark ? '#C4BFFF' : '#7FB342', border: `1px solid ${isDark ? 'rgba(30,91,224,0.2)' : 'rgba(30,91,224,0.12)'}` }}>
              <Globe className="h-3 w-3" /> {t('landing_v3.trust_languages', '10 langues détectées et traduites')}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium" style={{ background: isDark ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.06)', color: isDark ? '#86EFAC' : '#16A34A', border: `1px solid ${isDark ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.12)'}` }}>
              <Shield className="h-3 w-3" /> {t('landing_v3.trust_countries', '5 pays européens')}
            </span>
          </motion.div>
          {stats.visible && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 flex items-center justify-center gap-2 text-sm" style={{ color: ts }}>
              <div className="flex -space-x-2">{[V, '#5B4FD9', '#22C55E', '#EF4444', '#0EA5E9'].map((c, i) => <div key={i} className="w-7 h-7 rounded-full border-2 flex items-center justify-center text-white text-[10px] font-bold" style={{ background: c, borderColor: bg }} />)}</div>
              <Star className="h-4 w-4 text-yellow-400 fill-yellow-400" /> {stats.noteMoyenne}/5 · +{stats.utilisateurs} personnes
            </motion.div>
          )}
        </div>
      </section>

      {/* ═══ DOMAINES (5 blocs) — juste sous le hero ═══ */}
      <section className="py-12 sm:py-16 px-4" style={{ background: isDark ? '#0F0F1A' : '#F7F6FF' }} data-testid="landing-domains-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-2" style={{ color: tp }}>
            {t('landing_v3.domains_title', 'Dans quel domaine avez-vous besoin d\'aide ?')}
          </h2>
          <p className="text-center text-sm mb-8 sm:mb-10" style={{ color: ts }}>
            {t('landing_v3.domains_subtitle', 'Choisissez votre situation — Kadmi s\'occupe du reste en 30 secondes')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            {DOMAINS.map((d, i) => (
              <motion.div
                key={d.slug}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.4 }}
                whileHover={{ y: -4 }}
              >
                <Link
                  to={`/domaine/${d.slug}`}
                  data-testid={`landing-domain-${d.slug}`}
                  className="block h-full rounded-2xl p-5 sm:p-6 text-center transition-all"
                  style={{
                    background: cardBg,
                    border: `1px solid ${bd}`,
                    boxShadow: 'var(--shadow-card)',
                  }}
                >
                  <div
                    className="w-14 h-14 rounded-2xl mx-auto mb-3 flex items-center justify-center text-3xl"
                    style={{ background: `${d.color}18` }}
                  >
                    {d.icon}
                  </div>
                  <h3 className="font-heading text-base sm:text-lg mb-1" style={{ color: tp }}>
                    {d.title}
                  </h3>
                  <p className="text-xs leading-relaxed mb-3" style={{ color: ts }}>
                    {d.tagline}
                  </p>
                  <span
                    className="inline-flex items-center gap-1 text-xs font-semibold"
                    style={{ color: d.color }}
                  >
                    Voir les recours →
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/recours"
              data-testid="landing-recours-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
              style={{ color: V }}
            >
              {t('landing_v3.recours_see_all', 'Lire nos guides de recours administratifs')} →
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ BANDEAU SITUATIONS ═══ */}
      <section className="py-4 overflow-hidden" style={{ background: isDark ? '#1A1A2E' : '#EDE9FF' }}>
        <p className="text-center text-xs font-medium mb-2" style={{ color: ts }}>{t('landing_v3.bandeau_label')}</p>
        <div className="relative">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...CATS, ...CATS].map((cat, i) => (
              <span key={i} className="inline-flex items-center gap-1.5 mx-4 text-sm font-medium" style={{ color: tp }}>
                {t(`landing_v3.cat_${cat}`)}
              </span>
            ))}
          </div>
        </div>
        <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } } .animate-marquee { animation: marquee 25s linear infinite; } .animate-marquee:hover { animation-play-state: paused; }`}</style>
      </section>

      {/* ═══ TRUST BADGES (RGPD, Stripe, E2E, Suppression auto) ═══ */}
      <TrustBadges />

      {/* ═══ FEATURE SHOWCASE (style Qonto) ═══ */}
      <FeatureShowcase />

      {/* ═══ CHATBOT DEMO ANIMÉ ═══ */}
      <ChatbotDemo />

      {/* ═══ SITUATIONS RÉELLES ═══ */}
      <section id="comment" className="py-16 px-4 lb-section">
        {isDark && <div className="lb-layer"><div className="lb-noise" /></div>}
        {!isDark && <div className="lb-layer"><div className="lb-soft-bubble" /></div>}
        <div className="lb-content max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-2" style={{ color: tp }}>{t('landing_v3.situations_title')}</h2>
          <p className="text-center text-sm mb-10" style={{ color: ts }}>{t('landing_v3.situations_subtitle')}</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {['caf', 'amende', 'prefecture'].map((sit, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="rounded-2xl p-5" style={{ background: cardBg, border: `1px solid ${bd}`, boxShadow: 'var(--shadow-card)' }}>
                <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-3" style={{ background: isDark ? 'rgba(30,91,224,0.15)' : '#EDE9FF', color: V }}>{t(`landing_v3.cat_${sit}`)}</span>
                <p className="text-sm leading-relaxed mb-4" style={{ color: ts }}>{t(`landing_v3.sit_${sit}`)}</p>
                <span className="flex items-center gap-1 font-medium text-xs" style={{ color: '#22C55E' }}><CheckCircle className="h-3.5 w-3.5" /> {t('landing_v3.situations_result')}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ TÉMOIGNAGES ═══ */}
      <section className="py-16 px-4" style={{ background: isDark ? '#1A1A2E' : '#EDE9FF' }}>
        <h2 className="font-heading text-2xl sm:text-3xl text-center mb-10" style={{ color: tp }}>{t('landing_v3.testimonials_title', "Ce qu'ils en disent")}</h2>
        {visibleTemoignages.length > 0 && (
          <div className="max-w-5xl mx-auto grid sm:grid-cols-3 gap-4">
            {visibleTemoignages.map((t, i) => (
              <div key={i} className="rounded-2xl p-5" style={{ background: cardBg, border: `1px solid ${bd}` }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: V }}>{t.prenom?.[0]}</div>
                  <div><p className="font-semibold text-sm" style={{ color: tp }}>{t.prenom}{t.ville && ` · ${t.ville}`}</p><span className="text-xs" style={{ color: V }}>{t.situation}</span></div>
                </div>
                <div className="flex gap-0.5 mb-2">{[1,2,3,4,5].map(n => <Star key={n} className={`h-4 w-4 ${n <= t.note ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />)}</div>
                <p className="text-sm leading-relaxed" style={{ color: ts }}>{t.texte}</p>
              </div>
            ))}
          </div>
        )}
        <div className="max-w-5xl mx-auto mt-8 text-center">
          <p className="text-sm mb-3" style={{ color: ts }}>{t('landing_v3.testimonials_tagline', 'Des cas types concrets, vérifiables, pour illustrer ce que Kadmi fait pour vous.')}</p>
          <Link to="/temoignages" data-testid="landing-temoignages-link" className="inline-flex items-center gap-2 text-sm font-semibold hover:underline" style={{ color: V }}>
            {t('landing_v3.testimonials_see_all', 'Voir les cas types et exemples de recours')} →
          </Link>
        </div>
      </section>

      {/* ═══ COMMENT ÇA MARCHE ═══ */}
      <section className="py-16 px-4" style={{ background: isDark ? '#0F0F1A' : '#FFFFFF' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-10" style={{ color: tp }}>{t('landing_v3.comment_title')}</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[1, 2, 3].map((n, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -15 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.2 }} className="text-center sm:text-left">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm mx-auto sm:mx-0 mb-3" style={{ background: V }}>{n}</div>
                <h3 className="font-heading text-base mb-2" style={{ color: tp }}>{t(`landing_v3.step${n}_title`)}</h3>
                <p className="text-sm leading-relaxed" style={{ color: ts }}>{t(`landing_v3.step${n}_desc`)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ DÉMO INTERACTIVE ═══ */}
      <section className="py-16 px-4" style={{ background: isDark ? '#1A1A2E' : '#F7F6FF' }}>
        <div className="max-w-[600px] mx-auto">
          <div className="rounded-2xl p-6 sm:p-8" style={{ background: cardBg, border: `1px solid ${bd}`, boxShadow: 'var(--shadow-card)' }}>
            <h2 className="font-heading text-xl sm:text-2xl text-center mb-6" style={{ color: tp }}>{t('landing_v3.demo_title')}</h2>
            <textarea value={demoText} onChange={e => setDemoText(e.target.value)} placeholder={placeholders[phIdx]} rows={4}
              className="w-full rounded-xl p-4 text-sm resize-none outline-none transition-colors"
              style={{ background: inputBg, border: `1px solid ${bd}`, color: tp }} data-testid="demo-input" />
            <button onClick={handleDemo} disabled={!demoText.trim() || demoLoading}
              className="w-full mt-3 py-3.5 rounded-xl text-white font-semibold text-sm disabled:opacity-40 transition-all"
              style={{ background: V, boxShadow: demoText.trim() ? `0 4px 16px rgba(30,91,224,0.3)` : 'none' }} data-testid="demo-btn">
              {demoLoading ? <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> : <Zap className="h-4 w-4 inline mr-2" />}
              {t('landing_v3.demo_btn')}
            </button>
            <AnimatePresence>{demoResult && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-xl p-4 text-sm leading-relaxed" style={{ background: isDark ? 'rgba(34,197,94,0.08)' : 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', color: tp }}>
                <p className="mb-1"><CheckCircle className="h-4 w-4 inline mr-1 text-green-500" /> <strong>{t('landing_v3.demo_label_letter', 'Courrier identifié')} :</strong> {demoResult.resume}</p>
                <p className="mb-1"><Shield className="h-4 w-4 inline mr-1 text-blue-500" /> <strong>{t('landing_v3.demo_label_action', 'Action recommandée')} :</strong> {demoResult.action}</p>
                {demoResult.delai && <p><Clock className="h-4 w-4 inline mr-1 text-amber-500" /> <strong>{t('landing_v3.demo_label_delay', 'Délai')} :</strong> {demoResult.delai}</p>}

                {/* CTA: Générer un courrier-exemple */}
                {!sampleLetter && (
                  <button
                    onClick={handleSampleLetter}
                    disabled={sampleLetterLoading}
                    className="mt-4 w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    style={{ background: 'linear-gradient(135deg, #7FB342 0%, #AED581 100%)', color: '#FFFFFF', boxShadow: '0 4px 16px rgba(30,91,224,0.3)' }}
                    data-testid="sample-letter-btn"
                  >
                    {sampleLetterLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Génération du courrier-exemple…</> : <><Sparkles className="h-4 w-4" /> Voir un courrier-exemple (gratuit)</>}
                  </button>
                )}
                {sampleLetterError && (
                  <p className="mt-3 text-xs text-amber-600 dark:text-amber-400" data-testid="sample-letter-error">⚠️ {sampleLetterError}</p>
                )}

                <Link to="/register" className="block mt-3 text-center font-semibold text-sm" style={{ color: V }}>
                  {t('landing_v3.demo_result_cta')} <ArrowRight className="h-4 w-4 inline" />
                </Link>
              </motion.div>
            )}</AnimatePresence>

            {/* SAMPLE LETTER PREVIEW with paywall blur */}
            <AnimatePresence>{sampleLetter && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 rounded-xl overflow-hidden"
                style={{ background: cardBg, border: `1px solid ${bd}` }}
                data-testid="sample-letter-result"
              >
                {/* Header courrier */}
                <div className="px-5 pt-5 pb-3 border-b" style={{ borderColor: bd, background: isDark ? 'rgba(0,217,255,0.05)' : 'rgba(30,91,224,0.04)' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4" style={{ color: '#7FB342' }} />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#7FB342' }}>Courrier-exemple généré par KadmiAI</span>
                  </div>
                  <h4 className="font-heading text-base mb-1" style={{ color: tp }}>{sampleLetter.objet}</h4>
                  <p className="text-xs" style={{ color: ts }}>Destinataire : <strong>{sampleLetter.destinataire}</strong>{sampleLetter.delai_legal && <> · Délai : <strong>{sampleLetter.delai_legal}</strong></>}</p>
                </div>

                {/* Body teaser */}
                <div className="relative">
                  <div className="px-5 py-4 text-sm leading-relaxed whitespace-pre-line font-mono" style={{ color: tp, maxHeight: 280, overflow: 'hidden' }}>
                    {sampleLetter.preview_text}
                  </div>
                  {/* Paywall overlay */}
                  {sampleLetter.is_truncated && (
                    <div className="absolute inset-x-0 bottom-0 h-32 flex items-end justify-center pointer-events-none" style={{ background: `linear-gradient(180deg, transparent 0%, ${cardBg} 70%)` }}>
                      <div className="pointer-events-auto pb-4 px-4 text-center w-full">
                        <p className="text-xs mb-2 font-semibold flex items-center justify-center gap-1.5" style={{ color: ts }}>
                          <Lock className="h-3.5 w-3.5" /> Suite du courrier verrouillée ({sampleLetter.total_chars - sampleLetter.preview_chars} caractères restants)
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Argument juridique tease */}
                {sampleLetter.argument_juridique_cle && (
                  <div className="px-5 py-3 border-t flex items-start gap-2" style={{ borderColor: bd, background: isDark ? 'rgba(255,241,0,0.04)' : 'rgba(255,241,0,0.06)' }}>
                    <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#0EA5E9' }} />
                    <p className="text-xs leading-relaxed" style={{ color: tp }}>
                      <strong>Argument juridique :</strong> {sampleLetter.argument_juridique_cle}
                    </p>
                  </div>
                )}

                {/* CTA débloquer */}
                <div className="p-5 border-t" style={{ borderColor: bd, background: isDark ? 'rgba(30,91,224,0.06)' : 'rgba(30,91,224,0.04)' }}>
                  <Link to="/register?ref=sample-letter" data-testid="sample-letter-cta">
                    <button className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2" style={{ background: V, color: '#FFFFFF', boxShadow: '0 4px 16px rgba(30,91,224,0.35)' }}>
                      Débloquer le courrier complet <ArrowRight className="h-4 w-4" />
                    </button>
                  </Link>
                  <p className="text-[11px] text-center mt-2" style={{ color: ts }}>Inscription gratuite · 2 documents offerts · Sans CB</p>
                </div>

                {/* Bloc partage viral */}
                <div className="px-5 py-4 border-t" style={{ borderColor: bd }} data-testid="sample-letter-share-block">
                  <p className="text-xs text-center mb-3 font-semibold" style={{ color: ts }}>
                    <Share2 className="h-3.5 w-3.5 inline mr-1" /> Partagez KadmiAI à un proche qui galère avec sa paperasse
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    <a href={shareUrl('whatsapp')} target="_blank" rel="noopener noreferrer" data-testid="share-whatsapp" className="flex flex-col items-center gap-1 py-2 rounded-lg transition-all hover:scale-105" style={{ background: '#25D366', color: '#FFFFFF' }}>
                      <Phone className="h-4 w-4" />
                      <span className="text-[10px] font-semibold">WhatsApp</span>
                    </a>
                    <a href={shareUrl('twitter')} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="flex flex-col items-center gap-1 py-2 rounded-lg transition-all hover:scale-105" style={{ background: '#0F0F0F', color: '#FFFFFF' }}>
                      <span className="text-base font-bold">𝕏</span>
                      <span className="text-[10px] font-semibold">X</span>
                    </a>
                    <a href={shareUrl('linkedin')} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className="flex flex-col items-center gap-1 py-2 rounded-lg transition-all hover:scale-105" style={{ background: '#0A66C2', color: '#FFFFFF' }}>
                      <Mail className="h-4 w-4" />
                      <span className="text-[10px] font-semibold">LinkedIn</span>
                    </a>
                    <button onClick={handleShareCopy} data-testid="share-copy" className="flex flex-col items-center gap-1 py-2 rounded-lg transition-all hover:scale-105" style={{ background: shareCopied ? '#22c55e' : (isDark ? '#252538' : '#F0F0F8'), color: shareCopied ? '#FFFFFF' : tp, border: `1px solid ${bd}` }}>
                      {shareCopied ? <CheckCircle className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      <span className="text-[10px] font-semibold">{shareCopied ? 'Copié !' : 'Copier'}</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}</AnimatePresence>
          </div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="tarifs" className="py-16 px-4" style={{ background: isDark ? '#0F0F1A' : '#FFFFFF' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-2" style={{ color: tp }}>3 plans pour simplifier votre paperasse</h2>
          <p className="text-center text-sm mb-10" style={{ color: ts }}>2 documents + 2 courriers offerts. Sans engagement, sans CB.</p>
          <div className="grid sm:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {/* Standard */}
            <div className="rounded-2xl p-5 flex flex-col" style={{ background: cardBg, border: `1px solid ${bd}` }}>
              <h3 className="font-heading text-base mb-1" style={{ color: tp }}>Standard</h3>
              <p className="text-xs mb-3" style={{ color: ts }}>Pour les usages réguliers</p>
              <p className="mb-1"><span className="font-heading text-[2rem]" style={{ color: tp }}>1,99€</span><span className="text-sm" style={{ color: ts }}> /mois</span></p>
              <p className="text-xs mb-4" style={{ color: ts }}>ou 19,10€/an — économisez 20%</p>
              <ul className="space-y-2 mb-5 flex-1">
                {['5 documents par mois','Analyse IA (GPT-4o)','Courriers administratifs','Export PDF','Support email'].map((f,i)=>(
                  <li key={i} className="flex items-start gap-2 text-xs" style={{ color: ts }}><CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: V }} /> {f}</li>
                ))}
              </ul>
              <Link to="/register"><button className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all" style={{ border: `2px solid ${V}`, color: V, background: 'transparent' }}>Choisir Standard</button></Link>
            </div>
            {/* Pro (highlighted — Bleu Cinématique) */}
            <div className="rounded-2xl p-5 relative overflow-hidden text-white flex flex-col" style={{ background: 'linear-gradient(135deg, #7FB342 0%, #AED581 100%)' }}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <h3 className="font-heading text-base leading-tight">Pro</h3>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0" style={{ background: '#fff', color: '#7FB342' }} data-testid="pricing-pro-badge">
                  <Star className="h-2.5 w-2.5" />POPULAIRE
                </span>
              </div>
              <p className="text-xs text-white/70 mb-3">Le plus choisi</p>
              <p className="mb-1"><span className="font-heading text-[2rem]">4,99€</span><span className="text-white/60 text-sm"> /mois</span></p>
              <p className="text-xs text-white/70 mb-4">ou 47,90€/an — économisez 20%</p>
              <ul className="space-y-2 mb-5 flex-1">
                {['25 documents par mois','Vision IA (photos) + lecture PDF','Rappels intelligents (J-15, J-7, J-2)','Lecture vocale & Dictée','Support prioritaire 7j/7'].map((f,i)=>(
                  <li key={i} className="flex items-start gap-2 text-xs"><CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {f}</li>
                ))}
              </ul>
              <Link to="/register"><button className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all hover:-translate-y-0.5" style={{ background: '#fff', color: '#7FB342' }}>Choisir Pro <ArrowRight className="h-3.5 w-3.5 inline ml-1" /></button></Link>
            </div>
            {/* Famille */}
            <div className="rounded-2xl p-5 flex flex-col" style={{ background: cardBg, border: `1px solid ${bd}` }}>
              <h3 className="font-heading text-base mb-1" style={{ color: tp }}>Famille</h3>
              <p className="text-xs mb-3" style={{ color: ts }}>Pour les gros volumes</p>
              <p className="mb-1"><span className="font-heading text-[2rem]" style={{ color: tp }}>7,99€</span><span className="text-sm" style={{ color: ts }}> /mois</span></p>
              <p className="text-xs mb-4" style={{ color: ts }}>ou 76,70€/an — économisez 20%</p>
              <ul className="space-y-2 mb-5 flex-1">
                {['50 documents par mois','Toutes les fonctionnalités Pro','Vision + PDF + TTS + Rappels','Support prioritaire VIP 7j/7','Idéal multi-foyer ou indépendant'].map((f,i)=>(
                  <li key={i} className="flex items-start gap-2 text-xs" style={{ color: ts }}><CheckCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: V }} /> {f}</li>
                ))}
              </ul>
              <Link to="/register"><button className="w-full py-2.5 rounded-xl font-semibold text-sm transition-all" style={{ border: `2px solid ${V}`, color: V, background: 'transparent' }}>Choisir Famille</button></Link>
            </div>
          </div>
          <div className="text-center mt-6">
            <Link to="/pricing" className="text-sm underline" style={{ color: V }}>Voir tous les détails et comparer les plans →</Link>
          </div>
          <p className="text-center text-xs mt-4" style={{ color: ts }}>{t('landing_v3.security')}</p>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section id="faq" className="py-16 px-4 lb-section" style={{ background: isDark ? '#1A1A2E' : '#F7F6FF' }}>
        <div className="lb-layer">
          <div className="lb-faq-dot" style={{ width: 4, height: 4, top: '10%', left: '8%', background: `rgba(30,91,224,${isDark ? 0.2 : 0.1})`, animation: 'lbPulse 5s ease-in-out infinite' }} />
          <div className="lb-faq-dot" style={{ width: 3, height: 3, top: '30%', right: '12%', background: `rgba(30,91,224,${isDark ? 0.15 : 0.08})`, animation: 'lbPulse 7s ease-in-out infinite', animationDelay: '1s' }} />
          <div className="lb-faq-dot" style={{ width: 5, height: 5, top: '60%', left: '5%', background: `rgba(30,91,224,${isDark ? 0.18 : 0.06})`, animation: 'lbPulse 4s ease-in-out infinite', animationDelay: '2s' }} />
          <div className="lb-faq-dot" style={{ width: 3, height: 3, top: '80%', right: '20%', background: `rgba(30,91,224,${isDark ? 0.12 : 0.05})`, animation: 'lbPulse 6s ease-in-out infinite', animationDelay: '0.5s' }} />
          <div className="lb-faq-dot" style={{ width: 4, height: 4, top: '45%', left: '92%', background: `rgba(30,91,224,${isDark ? 0.15 : 0.07})`, animation: 'lbPulse 5.5s ease-in-out infinite', animationDelay: '1.5s' }} />
        </div>
        <div className="lb-content max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl text-center mb-10" style={{ color: tp }}>{t('landing_v3.faq_title')}</h2>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((n) => (
              <div key={n} className="rounded-xl overflow-hidden" style={{ background: cardBg, border: `1px solid ${bd}` }}>
                <button onClick={() => setOpenFaq(openFaq === n ? null : n)} className="w-full flex items-center justify-between p-4 text-left text-sm font-semibold" style={{ color: tp }}>
                  {t(`landing_v3.faq_q${n}`)}
                  <motion.div animate={{ rotate: openFaq === n ? 180 : 0 }}><ChevronDown className="h-4 w-4 shrink-0" style={{ color: ts }} /></motion.div>
                </button>
                <AnimatePresence>{openFaq === n && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }}>
                    <p className="px-4 pb-4 text-sm leading-relaxed" style={{ color: ts }}>{t(`landing_v3.faq_r${n}`)}</p>
                  </motion.div>
                )}</AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ URGENCE + CONVERSION ═══ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto rounded-3xl px-6 py-14 text-center text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${V}, #5B4FD9)` }}>
          <div className="lb-shimmer" />
          <div className="lb-glint-tl" />
          <div className="lb-glint-br" />
          <h2 className="font-heading text-2xl sm:text-3xl mb-3 leading-tight relative z-10">{t('landing_v3.urgence_title').split('\n').map((l, i) => <span key={i}>{i > 0 && <br />}{l}</span>)}</h2>
          <p className="text-white/80 text-sm mb-8 relative z-10">{t('landing_v3.urgence_subtitle')}</p>
          <Link to="/register" onClick={() => analytics.ctaEssayerGratuitement('urgence')}>
            <button className="px-8 py-4 rounded-[14px] font-semibold text-base transition-all hover:-translate-y-0.5 relative z-10" style={{ background: '#fff', color: V }}><Sparkles className="h-4 w-4 inline mr-2" />{t('landing_v3.urgence_cta')} <ArrowRight className="h-4 w-4 inline ml-1" /></button>
          </Link>
          <p className="text-white/60 text-xs mt-3 relative z-10">{t('landing_v3.urgence_micro')}</p>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="py-12 px-4 lb-section" style={{ background: '#0F0F1A' }}>
        <div className="lb-layer"><div className="lb-footer-bubble" /></div>
        <div className="lb-content max-w-5xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4"><KadmiLogo size={28} /><span className="font-heading text-white">KadmiAI</span></div>
              <p className="text-xs text-[#9998B8]">{t('landing_v3.footer_tagline')}</p>
            </div>
            <div><p className="text-xs font-semibold text-white mb-3">{t('landing.footer_product')}</p>{[{label: t('landing.footer_features'), h:'/features'},{label: t('landing.footer_pricing'), h:'/pricing'},{label: t('landing.footer_demo'), h:'/demo'},{label: 'Installer l\'app', h:'/installer'}].map(l => <Link key={l.label} to={l.h} className="block text-xs text-[#9998B8] mb-2 hover:text-white transition-colors">{l.label}</Link>)}</div>
            <div><p className="text-xs font-semibold text-white mb-3">{t('landing.footer_resources')}</p>{[{label:t('landing.footer_blog'),h:'/blog'},{label:t('landing.footer_models'),h:'/modeles'},{label:'Guides de recours',h:'/recours'},{label:'Témoignages',h:'/temoignages'},{label:'Comparer vs DocuGov',h:'/vs-docugov'}].map(l => <Link key={l.label} to={l.h} className="block text-xs text-[#9998B8] mb-2 hover:text-white transition-colors">{l.label}</Link>)}</div>
            <div><p className="text-xs font-semibold text-white mb-3">{t('landing.footer_legal')}</p>{[{label:t('landing.footer_terms'),h:'/terms'},{label:t('landing.footer_privacy'),h:'/privacy'},{label:'Mentions légales',h:'/mentions-legales'},{label:t('security.footer_link', 'Sécurité'),h:'/securite'}].map(l => <Link key={l.label} to={l.h} className="block text-xs text-[#9998B8] mb-2 hover:text-white transition-colors">{l.label}</Link>)}</div>
          </div>
          <div className="border-t border-[#2E2E45] pt-6 text-xs text-[#9998B8] text-center">{t('landing.footer_copyright')}</div>
        </div>
      </footer>

      {/* ═══ EXIT INTENT POPUP ═══ */}
      <AnimatePresence>{exitPopup && !isAuthenticated && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }} onClick={() => setExitPopup(false)} data-testid="exit-intent-overlay">
          <motion.div initial={{ scale: 0.85, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 20 }} transition={{ type: 'spring', damping: 25 }} className="max-w-md w-full rounded-2xl p-8 text-center relative" style={{ background: cardBg, boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setExitPopup(false)} className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center transition-colors hover:bg-black/10" style={{ color: ts }} data-testid="exit-intent-close"><X className="h-4 w-4" /></button>
            <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(30,91,224,0.12)' }}>
              <Mail className="h-6 w-6" style={{ color: V }} />
            </div>
            <h3 className="font-heading text-xl mb-2" style={{ color: tp }}>{t('landing_v3.exit_title')}</h3>
            <p className="text-sm mb-5" style={{ color: ts }}>{t('landing_v3.exit_subtitle')}</p>
            <Link to="/register" onClick={() => { setExitPopup(false); analytics.ctaEssayerGratuitement('exit_intent'); }}>
              <button className="w-full py-3.5 rounded-xl text-white font-semibold text-sm transition-all hover:-translate-y-0.5" style={{ background: V, boxShadow: '0 4px 16px rgba(30,91,224,0.35)' }} data-testid="exit-intent-cta">
                <Sparkles className="h-4 w-4 inline mr-2" />{t('landing_v3.exit_cta')}
              </button>
            </Link>
            <button onClick={() => setExitPopup(false)} className="mt-3 text-xs transition-colors hover:underline" style={{ color: ts }} data-testid="exit-intent-dismiss">{t('landing_v3.exit_dismiss')}</button>
          </motion.div>
        </motion.div>
      )}</AnimatePresence>

      {/* ═══ MOBILE STICKY CTA — retiré pour éviter la redondance avec header + hero CTA ═══ */}

      {/* ═══ PWA INSTALL BANNER (mobile, auto-show after 22s) ═══ */}
      {!isAuthenticated && <PwaInstallBanner />}

      {/* Schema.org FAQ */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({ "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": [1,2,3,4,5].map(n => ({ "@type": "Question", "name": t(`landing_v3.faq_q${n}`), "acceptedAnswer": { "@type": "Answer", "text": t(`landing_v3.faq_r${n}`) } })) }) }} />
    </div>
  );
};

export default LandingPageV2;
